import { fromMinorUnits } from './conversions';
import type { CurrencyMeta } from './types';

function floorToDecimals(value: number, decimals: number): number {
	const factor = Math.pow(10, decimals);
	return Math.floor(parseFloat((value * factor).toFixed(8))) / factor;
}

export const getLocaleSeparators = (locale: string) => {
	const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
	return {
		decimalSeparator: parts.find((p) => p.type === 'decimal')?.value || '.',
		thousandSeparator: parts.find((p) => p.type === 'group')?.value || ',',
	};
};

export function formatCurrency(
	minorUnits: number,
	meta: CurrencyMeta,
	locale: string,
	symbol?: string,
): string {
	const value = floorToDecimals(fromMinorUnits(minorUnits, meta), meta.displayDecimals);
	const formatter = new Intl.NumberFormat(locale, {
		style: 'currency',
		currency: meta.code,
		// narrowSymbol fallback for when no API symbol is available
		currencyDisplay: 'narrowSymbol',
		minimumFractionDigits: meta.displayDecimals,
		maximumFractionDigits: meta.displayDecimals,
	});
	const effectiveSymbol = meta.symbolOverride ?? meta.displaySymbol ?? symbol;
	if (!effectiveSymbol) return formatter.format(value);
	return formatter
		.formatToParts(value)
		.map((p) => (p.type === 'currency' ? effectiveSymbol : p.value))
		.join('');
}

export function formatCurrencyCompact(
	minorUnits: number,
	meta: CurrencyMeta,
	locale: string,
	threshold = 1_000_000,
	symbol?: string,
): string {
	const value = floorToDecimals(fromMinorUnits(minorUnits, meta), meta.displayDecimals);
	const hasDecimals = !Number.isInteger(value);

	const baseOptions: Intl.NumberFormatOptions = {
		style: 'currency',
		currency: meta.code,
		currencyDisplay: 'narrowSymbol',
		minimumFractionDigits: hasDecimals ? meta.displayDecimals : 0,
		maximumFractionDigits: hasDecimals ? meta.displayDecimals : 0,
	};

	const effectiveSymbol = meta.symbolOverride ?? meta.displaySymbol ?? symbol;

	const replace = (formatted: string) => {
		if (!effectiveSymbol) return formatted;
		return new Intl.NumberFormat(locale, baseOptions)
			.formatToParts(value)
			.map((p) => (p.type === 'currency' ? effectiveSymbol : p.value))
			.join('');
	};

	const full = replace(new Intl.NumberFormat(locale, baseOptions).format(value));

	if (Math.abs(value) < threshold) return full;

	const compact = new Intl.NumberFormat(locale, {
		...baseOptions,
		notation: 'compact',
		compactDisplay: 'short',
	})
		.formatToParts(value)
		.map((p) => (p.type === 'currency' ? (effectiveSymbol ?? p.value) : p.value))
		.join('');

	return compact !== full ? compact : full;
}
